import React, { useState } from 'react'
import Checkbox from 'core-system/Checkbox'
import Modal, { ModalBody, ModalFooter, ModalHeader } from 'core-system/Modal'
import Text from 'core-system/Text'
import pxToRem from 'core-system/utils/pxToRem'
import styled from 'styled-components'
import SecondaryButton from 'core-system/Button/SecondaryButton'
import PrimaryButton from 'core-system/Button/PrimaryButton'

const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
`

const CheckBoxes = styled.div`
  display: grid;
  grid-template-columns: repeat(
    auto-fill,
    minmax(${(props) => props.theme.pxToRem(187)}, 1fr)
  );
  grid-gap: 1rem;
  margin-top: 1rem;
`

const FilterContainer = styled.div`
  margin-bottom: 2rem;
`

export type FilterCheckbox = {
  label: string
  checked: boolean
  key: string
  value: any
}

const isSameFilter = (left: FilterCheckbox, right: FilterCheckbox) =>
  left.key === right.key && left.label === right.label

export type FilterObject = {
  header: string
  checkboxes: FilterCheckbox[]
}

export interface FilterModelProps {
  open: boolean
  closeModal: () => void
  handleApplyFilters: (selectedFilters: FilterCheckbox[]) => void
  handleFiltersReset: () => void
  filters: FilterObject[]
}

const FilterModel = React.memo((props: FilterModelProps) => {
  const { open, closeModal, handleApplyFilters, handleFiltersReset, filters } =
    props

  const [selectedFilters, setSelectedFilters] = useState<FilterCheckbox[]>([])

  if (!filters || filters.length === 0) {
    return null
  }

  return (
    <Modal open={open} onClose={() => closeModal()} width={pxToRem(752)}>
      <ModalHeader title='Filters' />
      <ModalBody padding='1.5rem 3.25rem'>
        {filters.map((filterObject) => (
          <FilterContainer key={filterObject.header}>
            <Text variant='action3'>{filterObject.header}</Text>
            <CheckBoxes>
              {filterObject.checkboxes.map((checkbox) => {
                return (
                  <Checkbox
                    key={checkbox.label}
                    checked={selectedFilters.some((filter) =>
                      isSameFilter(filter, checkbox)
                    )}
                    onClick={() => {
                      setSelectedFilters((prev) => {
                        if (
                          prev.some((filter) => isSameFilter(filter, checkbox))
                        ) {
                          return prev.filter(
                            (filter) => !isSameFilter(filter, checkbox)
                          )
                        }
                        return [...prev, checkbox]
                      })
                    }}
                    label={checkbox.label}
                    marginRight='1rem'
                  />
                )
              })}
            </CheckBoxes>
          </FilterContainer>
        ))}
      </ModalBody>
      <ModalFooter>
        <ButtonContainer>
          <SecondaryButton
            onClick={() => {
              setSelectedFilters([])
              handleFiltersReset()
            }}
            disabled={selectedFilters.length === 0}
          >
            Clear All
          </SecondaryButton>
          <PrimaryButton
            onClick={() => {
              if (selectedFilters.length === 0) {
                handleFiltersReset()
              } else {
                handleApplyFilters(selectedFilters)
              }
              closeModal()
            }}
          >
            Apply Filters
          </PrimaryButton>
        </ButtonContainer>
      </ModalFooter>
    </Modal>
  )
})

// Helps to identify component in React error logs
if (process.env.NODE_ENV !== 'production') {
  FilterModel.displayName = 'FilterModel'
}

export default FilterModel
